import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { brokerDebtDisbursementCall } from "../api/brokerDebtDisbursement";
import { formatNumber } from "../utils/utils";
import { NumberOfPerPage } from "../utils/globalVariable";

const NUMBER_PER_PAGE = NumberOfPerPage?.brokerDebtDisbursementHistory || 20;
const queryKey = "brokerDebtDisbursementHistory";

function formatDateOnly(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("vi-VN");
}

async function fetchBrokerDebtDisbursementHistory(employeeId, currentPage) {
  try {
    if (!employeeId) return { data: [], totalPages: 0, entries: 0 };

    const res = await brokerDebtDisbursementCall.getHistory(employeeId, {
      page: currentPage,
      limit: NUMBER_PER_PAGE,
    });

    const items = res?.items || [];
    const pagination = res?.pagination || {};
    const total = pagination?.total || 0;
    const totalPages = Math.ceil(total / NUMBER_PER_PAGE);
    const startEntry = (currentPage - 1) * NUMBER_PER_PAGE + 1;
    const endEntry = Math.min(currentPage * NUMBER_PER_PAGE, total);

    const data = items.map((item) => ({
      _id: item._id,
      apartmentCode: item.apartmentCode || "—",
      monthYear: item.monthYear || "—",
      disbursementDate: formatDateOnly(item.disbursementDate || item.createdAt),
      // Round về đồng (VNĐ) — không hiển thị phần thập phân.
      // 2 cột chỉ lưu trữ (Số trước thuế, Thuế) — "—" nếu bản ghi không có.
      amountBeforeTax:
        item.amountBeforeTax != null
          ? `${formatNumber(Math.round(Number(item.amountBeforeTax)))} VNĐ`
          : "—",
      taxAmount:
        item.taxAmount != null
          ? `${formatNumber(Math.round(Number(item.taxAmount)))} VNĐ`
          : "—",
      disbursedAmount:
        item.disbursedAmount != null
          ? `${formatNumber(Math.round(Number(item.disbursedAmount)))} VNĐ`
          : "0 VNĐ",
      // IMPORT = import file, còn lại = thanh toán tay
      source: item.source === "IMPORT" ? "Import" : "Thanh toán thủ công",
    }));

    return { data, totalPages, entries: total, startEntry, endEntry };
  } catch (error) {
    console.error("Error fetching brokerDebtDisbursementHistory:", error);
    return { data: [], totalPages: 0, entries: 0 };
  }
}

export function useBrokerDebtDisbursementHistory(employeeId) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [queryKey, employeeId, currentPage],
    queryFn: () => fetchBrokerDebtDisbursementHistory(employeeId, currentPage),
    enabled: !!employeeId,
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
  });

  return {
    historyData: data?.data || [],
    totalPages: data?.totalPages || 0,
    entries: data?.entries || 0,
    startEntry: data?.startEntry || 0,
    endEntry: data?.endEntry || 0,
    currentPage,
    setCurrentPage,
    refetch,
    isLoading,
    isRefetching,
  };
}
